import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Play, Pause, SkipBack, SkipForward, RotateCcw, Loader2,
  Subtitles, Gauge, Volume2, Sparkles, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPEEDS = [1, 1.25, 1.5, 2];

const KIND_STYLE = {
  question: { label: "The Question", accent: "text-sky-300", chip: "bg-sky-500/15 text-sky-200 border-sky-400/30" },
  concept: { label: "Concept", accent: "text-violet-300", chip: "bg-violet-500/15 text-violet-200 border-violet-400/30" },
  step: { label: "Solving", accent: "text-amber-300", chip: "bg-amber-500/15 text-amber-200 border-amber-400/30" },
  answer: { label: "Answer", accent: "text-emerald-300", chip: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  tip: { label: "Exam Tip", accent: "text-rose-300", chip: "bg-rose-500/15 text-rose-200 border-rose-400/30" },
};

const LOADING_STEPS = [
  "Question ko samajh raha hoon...",
  "Step-by-step solution likh raha hoon...",
  "Slides tayyar kar raha hoon...",
  "Voice record kar raha hoon...",
  "Almost done — video assemble ho raha hai...",
];

export const VideoExplanationPlayer = ({
  open,
  onOpenChange,
  questionId,
  sessionId,
  sessionType,
  questionIndex,
}) => {
  const token = localStorage.getItem("token");
  const audioRef = useRef(null);
  const fallbackTimer = useRef(null);

  const [language, setLanguage] = useState("hinglish");
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [error, setError] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [captions, setCaptions] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [finished, setFinished] = useState(false);

  const generate = useCallback(async (lang) => {
    setLoading(true);
    setError(null);
    setVideo(null);
    setSlideIdx(0);
    setElapsed(0);
    setFinished(false);
    try {
      const res = await axios.post(
        `${API}/exam-prep/video/generate`,
        {
          language: lang,
          question_id: questionId || null,
          session_id: sessionId || null,
          session_type: sessionType || null,
          question_index: questionIndex ?? null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVideo(res.data);
      setPlaying(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Video explanation banane me dikkat aayi. Dobara try karein.");
    } finally {
      setLoading(false);
    }
  }, [questionId, sessionId, sessionType, questionIndex, token]);

  // Kick off generation when the modal opens
  useEffect(() => {
    if (open && !video && !loading && !error) generate(language);
  }, [open]);

  // Reset everything on close
  useEffect(() => {
    if (open) return;
    if (audioRef.current) audioRef.current.pause();
    if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    setPlaying(false);
    setVideo(null);
    setError(null);
    setSlideIdx(0);
    setFinished(false);
  }, [open]);

  // Cycle loader copy
  useEffect(() => {
    if (!loading) return;
    setLoadStep(0);
    const t = setInterval(() => setLoadStep((s) => (s + 1) % LOADING_STEPS.length), 4000);
    return () => clearInterval(t);
  }, [loading]);

  const slides = video?.slides || [];
  const slide = slides[slideIdx] || null;
  const isLast = slideIdx >= slides.length - 1;

  const goToSlide = (idx) => {
    if (idx < 0 || idx >= slides.length) return;
    setSlideIdx(idx);
    setElapsed(0);
    setDuration(0);
    setFinished(false);
  };

  const handleEnded = () => {
    if (isLast) {
      setPlaying(false);
      setFinished(true);
    } else {
      goToSlide(slideIdx + 1);
    }
  };

  // Drive playback whenever slide / play state / speed changes
  useEffect(() => {
    const el = audioRef.current;
    if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    if (!slide) return;

    if (!slide.audio_url) {
      // No narration for this slide — hold it briefly, then move on
      if (playing) fallbackTimer.current = setTimeout(handleEnded, 7000);
      return;
    }
    if (!el) return;
    el.playbackRate = speed;
    if (playing) {
      el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  }, [slideIdx, playing, speed, video]);

  const revealedBullets = (() => {
    if (!slide) return 0;
    const total = slide.bullets?.length || 0;
    if (!slide.audio_url || !duration) return total;
    return Math.max(1, Math.min(total, Math.ceil(((elapsed + 0.6) / duration) * total)));
  })();

  const kind = KIND_STYLE[slide?.kind] || KIND_STYLE.step;
  const slideProgress = duration ? Math.min(100, (elapsed / duration) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[95vw] sm:max-w-4xl p-0 sm:p-0 gap-0 overflow-hidden bg-slate-950 border-slate-800"
        data-testid="video-explanation-modal"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-800 bg-slate-900">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-indigo-400 font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Video Explanation
            </p>
            <p className="text-sm text-slate-200 font-semibold truncate" data-testid="video-title">
              {video?.title || "Preparing your explanation"}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0 mr-8">
            {["hinglish", "english"].map((lang) => (
              <button
                key={lang}
                onClick={() => {
                  if (lang === language) return;
                  setLanguage(lang);
                  generate(lang);
                }}
                disabled={loading}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  language === lang
                    ? "bg-indigo-500 text-white"
                    : "bg-slate-800 text-slate-400 hover:text-slate-200"
                }`}
                data-testid={`video-lang-${lang}-btn`}
              >
                {lang === "hinglish" ? "Hinglish" : "English"}
              </button>
            ))}
          </div>
        </div>

        {/* Stage */}
        <div className="px-5 pt-5">
          <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-950 to-indigo-950 min-h-[300px] sm:min-h-[340px]">
            {loading && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center"
                data-testid="video-generating-loader"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-indigo-500/20 blur-xl animate-pulse" />
                  <Loader2 className="w-10 h-10 text-indigo-400 animate-spin relative" />
                </div>
                <p className="text-slate-200 text-sm font-medium">{LOADING_STEPS[loadStep]}</p>
                <p className="text-slate-500 text-xs">
                  Pehli baar 15-25 second lagta hai. Iske baad ye video sabke liye instant hai.
                </p>
              </div>
            )}

            {!loading && error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
                <AlertTriangle className="w-9 h-9 text-amber-400" />
                <p className="text-slate-300 text-sm">{error}</p>
                <Button
                  size="sm"
                  onClick={() => generate(language)}
                  className="bg-indigo-600 hover:bg-indigo-500"
                  data-testid="video-retry-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Retry
                </Button>
              </div>
            )}

            {!loading && !error && slide && (
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between mb-5">
                  <Badge className={`border ${kind.chip} text-[11px] tracking-wide`} data-testid="video-slide-kind">
                    {kind.label}
                  </Badge>
                  <span className="text-xs text-slate-500 font-mono">
                    {slideIdx + 1} / {slides.length}
                  </span>
                </div>

                <h3
                  className={`text-xl sm:text-2xl font-bold ${kind.accent} leading-snug mb-5`}
                  data-testid="video-slide-title"
                >
                  {slide.title}
                </h3>

                {slide.formula && (
                  <div className="inline-block mb-5 px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 font-mono text-sm text-emerald-300">
                    {slide.formula}
                  </div>
                )}

                <ul className="space-y-3" data-testid="video-slide-bullets">
                  {(slide.bullets || []).map((b, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-3 transition-all duration-500 ${
                        i < revealedBullets ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                      }`}
                    >
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 bg-current ${kind.accent}`} />
                      <span className="text-slate-200 text-sm sm:text-base leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>

                {finished && (
                  <div className="mt-6 flex items-center gap-2 text-emerald-400 text-sm font-medium">
                    <Sparkles className="w-4 h-4" /> Explanation complete — samajh aa gaya?
                  </div>
                )}
              </div>
            )}

            {/* Slide progress bar */}
            {!loading && !error && slides.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-sky-400 transition-[width] duration-200"
                  style={{ width: `${slideProgress}%` }}
                />
              </div>
            )}
          </div>

          {/* Captions */}
          {!loading && !error && slide && captions && (
            <p
              className="mt-3 text-[13px] text-slate-400 italic leading-relaxed"
              data-testid="video-captions-text"
            >
              “{slide.narration}”
            </p>
          )}
        </div>

        {/* Controls */}
        <div className="px-5 py-4 mt-2 border-t border-slate-800 bg-slate-900">
          <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                title={s.title}
                className={`h-1.5 rounded-full transition-all shrink-0 ${
                  i === slideIdx ? "w-8 bg-indigo-400" : i < slideIdx ? "w-4 bg-slate-500" : "w-4 bg-slate-700"
                }`}
                data-testid={`video-slide-dot-${i + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 h-9 w-9"
                onClick={() => goToSlide(slideIdx - 1)}
                disabled={!video || slideIdx === 0}
                data-testid="video-prev-slide-btn"
              >
                <SkipBack className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                className="bg-indigo-600 hover:bg-indigo-500 h-11 w-11 rounded-full"
                onClick={() => {
                  if (finished) {
                    goToSlide(0);
                    setPlaying(true);
                    return;
                  }
                  setPlaying((p) => !p);
                }}
                disabled={!video}
                data-testid="video-play-pause-btn"
              >
                {finished ? (
                  <RotateCcw className="w-5 h-5" />
                ) : playing ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 h-9 w-9"
                onClick={() => goToSlide(slideIdx + 1)}
                disabled={!video || isLast}
                data-testid="video-next-slide-btn"
              >
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800 text-xs"
                onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
                data-testid="video-speed-btn"
              >
                <Gauge className="w-3.5 h-3.5 mr-1" /> {speed}x
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className={`text-xs hover:bg-slate-800 ${captions ? "text-indigo-300" : "text-slate-500"}`}
                onClick={() => setCaptions((c) => !c)}
                data-testid="video-captions-toggle"
              >
                <Subtitles className="w-3.5 h-3.5 mr-1" /> Captions
              </Button>
              {video && !video.has_audio && (
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-400/30 text-[11px]">
                  <Volume2 className="w-3 h-3 mr-1" /> Voice unavailable
                </Badge>
              )}
            </div>
          </div>
        </div>

        {slide?.audio_url && (
          <audio
            ref={audioRef}
            key={`${video?.id}-${slideIdx}`}
            src={`${process.env.REACT_APP_BACKEND_URL}${slide.audio_url}`}
            preload="auto"
            onEnded={handleEnded}
            onTimeUpdate={(e) => setElapsed(e.target.currentTime)}
            onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
            onError={handleEnded}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoExplanationPlayer;
